import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";
import { ChevronLeft, FileDown, Lock, Loader2, Pencil, Plus, RefreshCw, Sheet, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORY_LABELS, downloadContract, euro, fmtRange, imageUri, SIZES } from "@/lib/noleggio";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { StatusBadges } from "@/components/StatusBadges";
import AdminCalendar from "@/components/AdminCalendar";
import AdminReviews from "@/components/AdminReviews";
import AdminMergeDuplicates from "@/components/AdminMergeDuplicates";
import AdminSheetLink from "@/components/AdminSheetLink";

const FILTERS = [["all", "Tutte"], ["confirmed", "Confermate"], ["pending_payment", "In attesa"], ["completed", "Completate"], ["cancelled", "Annullate"]];

export default function Admin() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin" color="var(--brand-primary)" /></div>;
  if (!user) return <Navigate to="/accedi?next=/admin" replace />;
  if (user.role !== "admin") {
    return (
      <div className="py-24 flex flex-col items-center gap-3 text-[var(--muted)]" data-testid="admin-only">
        <Lock size={44} /> {t("admin_only")}
      </div>
    );
  }
  return <AdminPanel />;
}

function AdminPanel() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("bikes");
  const [filter, setFilter] = useState("all");
  const [toDelete, setToDelete] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);

  const stats = useQuery({ queryKey: ["admin-stats"], queryFn: () => api("/admin/stats") });
  const bikes = useQuery({ queryKey: ["bikes", "all"], queryFn: () => api("/bikes") });
  const bookings = useQuery({ queryKey: ["admin-bookings", filter], queryFn: () => api(`/admin/bookings${filter === "all" ? "" : `?status=${filter}`}`) });
  const sheet = useQuery({ queryKey: ["sheet-status"], queryFn: () => api("/admin/sheet/status") });

  const refresh = () => ["admin-stats", "admin-bookings", "bikes", "sheet-status"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const sync = useMutation({ mutationFn: () => api("/admin/sheet/sync", { method: "POST" }), onSuccess: () => { toast.success("Foglio sincronizzato"); refresh(); }, onError: (e) => toast.error(e.message) });
  const patch = useMutation({ mutationFn: ({ id, body }) => api(`/admin/bookings/${id}`, { method: "PATCH", body }), onSuccess: () => { toast.success("Prenotazione aggiornata"); refresh(); }, onError: (e) => toast.error(e.message) });
  const del = useMutation({ mutationFn: (id) => api(`/admin/bikes/${id}`, { method: "DELETE" }), onSuccess: () => { toast.success("Bici eliminata"); refresh(); }, onError: (e) => toast.error(e.message), onSettled: () => setToDelete(null) });

  const s = stats.data;
  const sh = sheet.data;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 pt-6">
      <div className="flex items-center gap-2 mb-5">
        <button data-testid="admin-back" onClick={() => nav("/profilo")} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white"><ChevronLeft /></button>
        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">Gestione negozio</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Bici in flotta" value={s?.total_units} id="stat-units" />
        <Stat label="Noleggi oggi" value={s?.today_bookings} id="stat-today" />
        <Stat label="Prenotazioni attive" value={s?.active_bookings} id="stat-active" />
        <Stat label="Incassato" value={s ? euro(s.revenue_paid) : undefined} id="stat-revenue" />
      </div>
      <div data-testid="sheet-sync-card" className="mn-card p-4 mt-3 flex items-center gap-3">
        <Sheet size={22} color="var(--brand-primary)" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">Foglio Google "modulo noleggio"</p>
          <p data-testid="sheet-sync-status" className="text-xs text-[var(--text-2)] truncate">
            {sh?.synced_at ? `Ultima sincronizzazione ${new Date(sh.synced_at).toLocaleTimeString("it-IT").slice(0, 5)} · ${sh.rentals ?? 0} noleggi dal foglio` : "Mai sincronizzato"}
          </p>
          <p className={`text-xs font-bold ${sh?.write_enabled ? "text-[var(--brand)]" : "text-[#c97a00]"}`}>{sh?.write_enabled ? "Scrittura sul foglio attiva" : "Le prenotazioni del sito non vengono ancora scritte nel foglio"}</p>
        </div>
        <button data-testid="sheet-link-open" onClick={() => setLinkOpen(true)} className={`mn-btn !min-h-[42px] text-sm ${sh?.write_enabled ? "mn-btn-ghost" : ""}`}>{sh?.write_enabled ? "Ricollega" : "Collega"}</button>
        <button data-testid="sheet-sync-button" onClick={() => sync.mutate()} disabled={sync.isPending} className="w-11 h-11 rounded-full bg-[var(--brand-secondary)] flex items-center justify-center hover:scale-105 transition-transform" title="Sincronizza">
          <RefreshCw size={19} className={sync.isPending ? "animate-spin" : ""} />
        </button>
      </div>
      <AdminReviews />
      {linkOpen && <AdminSheetLink onClose={() => setLinkOpen(false)} />}

      <div className="flex bg-white rounded-xl p-1 mt-5 max-w-md">
        {[["bikes", "Bici"], ["bookings", "Prenotazioni"], ["calendar", "Calendario"]].map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`flex-1 h-10 rounded-lg text-sm font-semibold transition-colors ${tab === k ? "bg-[var(--brand-primary)] text-white" : "text-[var(--muted)]"}`}>{l}</button>
        ))}
      </div>

      {tab === "calendar" ? <AdminCalendar /> : tab === "bikes" ? (
        <>
          <AdminMergeDuplicates bikes={bikes.data ?? []} />
          <div className="flex justify-end mt-4">
            <button data-testid="admin-add-bike" className="mn-btn" onClick={() => nav("/admin/bici/nuova")}><Plus size={18} /> Aggiungi bici</button>
          </div>
          <div data-testid="admin-bike-list" className="grid md:grid-cols-2 gap-3 mt-4">
            {bikes.isLoading && <Loader2 className="animate-spin" color="var(--brand-primary)" />}
            {(bikes.data ?? []).map((b) => (
              <div key={b.id} data-testid={`admin-bike-${b.id}`} className="mn-card p-4 flex flex-col gap-3">
                <div className="flex gap-3 items-center">
                  <img src={imageUri(b.image_url)} alt="" className="w-16 h-16 rounded-xl object-cover bg-[var(--surface-3)]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{b.name}</p>
                    <p className="text-sm text-[var(--text-2)]">{CATEGORY_LABELS[b.category]} · {euro(b.price_day)}/giorno</p>
                    <p className="text-xs text-[var(--muted)] flex gap-3 mt-0.5">{SIZES.map((sz) => <span key={sz}>{sz}: <b className="text-[var(--on-surface)]">{b.stock?.[sz] ?? 0}</b></span>)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`admin-edit-bike-${b.id}`} className="mn-btn mn-btn-ghost flex-1 !min-h-[44px]" onClick={() => nav(`/admin/bici/${b.id}`)}><Pencil size={16} /> Modifica</button>
                  <button data-testid={`admin-delete-bike-${b.id}`} className="mn-btn mn-btn-ghost flex-1 !min-h-[44px] !text-[var(--error)]" onClick={() => setToDelete(b)}><Trash2 size={16} /> Elimina</button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="mn-hscroll mt-4">
            {FILTERS.map(([k, l]) => <button key={k} data-testid={`admin-filter-${k}`} onClick={() => setFilter(k)} className={`mn-chip ${filter === k ? "mn-chip-on" : ""}`}>{l}</button>)}
          </div>
          <div data-testid="admin-booking-list" className="flex flex-col gap-3 mt-4">
            {bookings.isLoading && <Loader2 className="animate-spin" color="var(--brand-primary)" />}
            {!bookings.isLoading && (bookings.data ?? []).length === 0 && <p className="text-[var(--muted)] text-center py-10">Nessuna prenotazione</p>}
            {(bookings.data ?? []).map((b) => (
              <div key={b.id} data-testid={`admin-booking-${b.id}`} className="mn-card p-4 flex flex-col gap-2">
                <div className="flex justify-between gap-3"><p className="font-bold truncate">{b.bike_name} · {b.size}</p><p className="font-extrabold">{euro(b.total)}</p></div>
                <p className="text-sm text-[var(--text-2)]">{fmtRange(b)}</p>
                <p className="text-sm text-[var(--text-2)] break-words">{b.user_name}{b.user_email ? ` · ${b.user_email}` : ""}{b.phone ? ` · ${b.phone}` : ""}</p>
                {b.source === "sheet" ? <p className="text-xs font-bold text-[var(--brand)]">Dal foglio {b.sheet_id ?? ""} · {b.unit_id ?? ""}</p>
                  : b.unit_id && <p className="text-xs font-bold text-[var(--brand)]">Bici {b.unit_id}{b.sheet_id ? ` · ${b.sheet_id}` : ""}</p>}
                <StatusBadges booking={b} showPaymentWhenCancelled />
                {b.contract_version && (
                  <button data-testid={`admin-contract-${b.id}`} className="self-start text-sm font-semibold text-[var(--brand-primary)] flex items-center gap-1.5 py-1" onClick={() => downloadContract(b).catch((e) => toast.error(e.message))}>
                    <FileDown size={16} /> Contratto PDF
                  </button>
                )}
                {b.source !== "sheet" && b.status !== "cancelled" && b.status !== "completed" && (
                  <div className="flex gap-2 flex-wrap mt-1">
                    {b.payment_status !== "paid" && <button data-testid={`admin-mark-paid-${b.id}`} className="mn-btn mn-btn-ghost flex-1 !min-h-[42px] text-sm" onClick={() => patch.mutate({ id: b.id, body: { payment_status: "paid", status: "confirmed" } })}>Segna pagata</button>}
                    <button data-testid={`admin-complete-${b.id}`} className="mn-btn mn-btn-ghost flex-1 !min-h-[42px] text-sm" onClick={() => patch.mutate({ id: b.id, body: { status: "completed" } })}>Riconsegnata</button>
                    <button data-testid={`admin-cancel-${b.id}`} className="mn-btn mn-btn-ghost flex-1 !min-h-[42px] text-sm !text-[var(--error)]" onClick={() => patch.mutate({ id: b.id, body: { status: "cancelled" } })}>Annulla</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {toDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center" onClick={() => setToDelete(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-6 flex flex-col gap-3 mn-rise">
            <h2 className="text-xl font-extrabold">Eliminare {toDelete.name}?</h2>
            <p className="text-sm text-[var(--muted)]">La bici sparirà dal catalogo. Le prenotazioni esistenti restano.</p>
            <button data-testid="confirm-delete-bike" className="mn-btn mn-btn-danger" disabled={del.isPending} onClick={() => del.mutate(toDelete.id)}>Elimina</button>
            <button data-testid="dismiss-delete-bike" className="mn-btn mn-btn-ghost" onClick={() => setToDelete(null)}>Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, id }) {
  return (
    <div className="rounded-2xl bg-[var(--brand-tertiary)] p-4">
      <p data-testid={id} className="text-2xl font-extrabold text-[var(--brand-primary)]">{value ?? "—"}</p>
      <p className="text-xs text-[var(--text-2)] mt-0.5">{label}</p>
    </div>
  );
}
